// Libraries
import 'dayjs/locale/pt'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
// MUI
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
// Types
import { Services, KmsVehicle } from '../../../../../store/slices/timesheet/timesheetPageSlice'
// Hooks
import { useGetTimesheetQuery } from '../../../../../utils/hooks/useGetTimesheetQuery'
import { useIsTimesheetApproved } from '../../../../../utils/hooks'
import { TimesheetStoreState } from '../../../../../store/timesheetStore'
// Components
import ServiceSummary from './ServiceSummary'
import ServiceDetails from './ServiceDetails'

interface ServiceProps {
	serviceDetail: Services
	instance: number
}

const DEFAULT_KMS_VEHICLES: KmsVehicle[] = [
	{
		kmsIni: 0,
		kmsEnd: 0,
		inEmpty: false,
	},
]

function Service({ serviceDetail, instance }: Readonly<ServiceProps>): React.JSX.Element {
	const { isFetching } = useGetTimesheetQuery()
	const isTimeSheetApproved = useIsTimesheetApproved()
	const kmsVehiclesFromStore = useSelector((state: TimesheetStoreState) => {
		const serviceId = serviceDetail.service.serviceId

		if (isTimeSheetApproved) {
			const approvalService =
				state.timesheetApprovalDetailsAccept.services.find(service => service.serviceId === serviceId) ??
				state.timesheetApprovalDetailsAccept.services[instance]

			if (Array.isArray(approvalService?.kmsVehicles)) {
				return approvalService.kmsVehicles as KmsVehicle[]
			}

			return undefined
		}

		const service =
			state.timesheetPage.services.find(item => item.service.serviceId === serviceId) ??
			state.timesheetPage.services[instance]

		if (service?.serviceExecution === null && Array.isArray(service?.service?.kmsVehicles)) {
			return service?.service?.kmsVehicles as KmsVehicle[]
		}

		if (Array.isArray(service?.serviceExecution?.kmsVehicles)) {
			return service?.serviceExecution?.kmsVehicles as KmsVehicle[]
		}

		return undefined
	})

	const kmsVehicles = useMemo(() => {
		if (isFetching) {
			return DEFAULT_KMS_VEHICLES
		}

		return kmsVehiclesFromStore ?? DEFAULT_KMS_VEHICLES
	}, [isFetching, kmsVehiclesFromStore])

	return (
		<li
			style={{
				margin: 0,
				padding: 0,
				display: 'grid',
			}}
		>
			<Accordion disableGutters>
				<AccordionSummary
					sx={{
						padding: 0,
						backgroundColor: 'transparent',
						cursor: 'default !important',
						borderTop: '1px solid rgba(134, 149, 175, 0.15)',
						'& *.Mui-expanded': {
							backgroundColor: '#F5F7FC !important',
						},
						'& .MuiAccordionSummary-content': {
							margin: 0,
							padding: '12px 0',
						},
						'& .MuiAccordionSummary-expandIconWrapper': {
							height: 'inherit',
							margin: 0,
							padding: '12px 20px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							alignSelf: 'stretch',
						},
					}}
					expandIcon={<ExpandMoreIcon sx={{ color: 'var(--primary)' }} />}
				>
					<ServiceSummary
						serviceDetail={serviceDetail}
						instance={instance}
						kmsVehicles={kmsVehicles}
					/>
				</AccordionSummary>
				<AccordionDetails sx={{ margin: 0, padding: 0 }}>
					<ServiceDetails
						serviceDetail={serviceDetail}
						instance={instance}
						kmsVehicles={kmsVehicles}
					/>
				</AccordionDetails>
			</Accordion>
		</li>
	)
}

export default Service
